import { Avatar, Group, Stack, Text } from 'components'
import { ProjectClassroomField } from 'components/ProjectClassroomField'
import { getInitials } from 'helpers'
import { useArcTranslation } from 'hooks'
import { IProjectNew } from 'interfaces'
import { ITeacher } from 'interfaces/ITeacher'

interface ArcProjectInfoMetaProps {
  readonly project: IProjectNew
  readonly teachers: ITeacher[] | null
  // AR-882: unique-lessons total from the session-meetings list pagination.
  // Overrides project.totalMeetings, which the backend inflates to the
  // activities count for student/guardian profiles.
  readonly lessonsCount?: number
}

const MetaField = ({ label, value }: { readonly label: string; readonly value: string }) => (
  <Stack spacing={2} miw={120}>
    <Text size='sm' color='dimmed'>
      {label}
    </Text>
    <Text weight={500}>{value}</Text>
  </Stack>
)

const getGradeName = (project: IProjectNew): string => {
  if (!project.grades || project.grades.length === 0) return '—'
  return project.grades.map(g => g.name).join(', ')
}

const TEACHER_AVATAR_LIMIT = 4

const StackedTeachers = ({ teachers }: { readonly teachers: readonly ITeacher[] }) => {
  const visible = teachers.slice(0, TEACHER_AVATAR_LIMIT)
  const overflow = teachers.length - visible.length
  return (
    <Avatar.Group spacing='sm'>
      {visible.map(t => (
        <Avatar key={t.id} src={t.avatar || null} radius='xl' size={28} color='blue'>
          {getInitials(t.name)}
        </Avatar>
      ))}
      {overflow > 0 && (
        <Avatar radius='xl' size={28}>
          +{overflow}
        </Avatar>
      )}
    </Avatar.Group>
  )
}

export const ArcProjectInfoMeta = ({ project, teachers, lessonsCount }: ArcProjectInfoMetaProps) => {
  const { t } = useArcTranslation()
  const isApplication = project.kind !== 'template'
  const tutor = project.tutorMain
  const allTeachers = teachers ?? []
  const showTeachersGroup = allTeachers.length > 0

  return (
    <Group position='apart' align='flex-start' mt='md' noWrap={false} w='100%'>
      <Group spacing='xl' align='flex-start'>
        {isApplication && (
          <MetaField
            label={t('arc_project_info.class')}
            value={project.groupName || t('new_project_view.arc_no_group_added')}
          />
        )}
        <MetaField label={t('arc_project_info.grade')} value={getGradeName(project)} />
        <MetaField label={t('arc_project_info.lessons')} value={String(lessonsCount ?? project.totalMeetings ?? 0)} />
        {/* AR-670: Google Classroom moved from a standalone card to this row. */}
        <ProjectClassroomField project={project} />
      </Group>

      {isApplication && (
        <Group spacing='xl' align='flex-start'>
          {tutor && tutor.id !== '' && (
            <Stack spacing={2}>
              <Text size='sm' color='dimmed'>
                {t('arc_project_info.created_by')}
              </Text>
              <Group spacing='xs' noWrap>
                <Avatar src={tutor.avatar || null} radius='xl' size={24} color='blue'>
                  {getInitials(tutor.name)}
                </Avatar>
                <Text weight={500}>{tutor.name}</Text>
              </Group>
            </Stack>
          )}
          {showTeachersGroup && (
            <Stack spacing={2}>
              <Text size='sm' color='dimmed'>
                {t('arc_project_info.project_teachers')}
              </Text>
              <StackedTeachers teachers={allTeachers} />
            </Stack>
          )}
        </Group>
      )}
    </Group>
  )
}
